import { useEffect, useState } from "react";

import { WebSocketStatus } from "@hocuspocus/provider";
import * as Sentry from "@sentry/nextjs";
import { reaction, runInAction } from "mobx";
import * as Y from "yjs";

import { devTools } from "@/components/debug-panel/DevToolsStore";
import { AwarenessModel } from "@/models/AwarenessModel";
import { DocumentModel } from "@/models/DocumentModel";
import { useStore } from "@/store";
import { InvalidSchemaVersionError } from "@/utils/migrateDocVersion";
import { getYjsProvider } from "@/utils/yjs/provider";

import { useFeatureFlag } from "./useFeatureFlag";

import type { Document } from "@/api";
import type { HocuspocusProvider, onAuthenticationFailedParameters } from "@hocuspocus/provider";

export type UseDocError =
  | {
      type: "noReadPermissions";
    }
  | {
      type: "invalidSchemaVersion" | "networkError";
      message: string;
    };

/**
 * Create a doc synced to the remote Y.Doc.
 * If this is an empty document, it will create an empty line.
 * TODO: Move all this logic to DocumentModel and DocumentApiModel
 * https://linear.app/corca-research/issue/D-1318/move-stuff-from-usedoc-to-documentmodel
 */
export function useDoc(
  docId: string,
  prefetchedDoc?: Document,
): {
  doc?: DocumentModel;
  error?: UseDocError;
} {
  const [error, setError] = useState<UseDocError | undefined>(undefined);
  const [doc, setDoc] = useState<DocumentModel | undefined>(undefined);

  const [yDoc, setYDoc] = useState<Y.Doc | undefined>(undefined);
  const [awareness, setAwareness] = useState<AwarenessModel | undefined>(undefined);
  const [yProvider, setYProvider] = useState<HocuspocusProvider | undefined>(undefined);
  const rootStore = useStore();

  const computedNodesEnabled = useFeatureFlag("computed-nodes");

  const { auth } = rootStore;

  // Step 1: Initialize yDoc via websocket.
  // If the document is writable, it will initialize awareness.
  // Wait until Firebase auth has resolved (with or without a user) before connecting to Yjs.
  useEffect(() => {
    if (!auth.authTokenResolved) {
      return undefined;
    }

    const newYDoc = new Y.Doc();

    let cleanupReconnect: () => void = () => {};

    // TODO: reconnect on auth changes
    // https://linear.app/corca-research/issue/D-1319/reconnect-to-yjs-server-on-auth-changes-login-logout
    const provider = getYjsProvider(docId, newYDoc);

    // Ensure doc is only set after first sync
    provider.on("synced", () => {
      setYDoc(newYDoc);
    });

    // Reconnect management (online/visibility/disconnect) with backoff
    let reconnectTimeout: NodeJS.Timeout | undefined;
    let attempt = 0;

    const isReadyToReconnect = () => navigator.onLine && document.visibilityState === "visible";

    const tryConnect = () => {
      if (!isReadyToReconnect()) return;
      const { status } = provider.configuration.websocketProvider;
      if (status === WebSocketStatus.Connected || status === WebSocketStatus.Connecting) {
        return;
      }
      try {
        // Refresh token lazily via provider's token function
        provider.connect();
      } catch (_) {
        // ignore, schedule retry
      }
    };

    const resetBackoff = () => {
      attempt = 0;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = undefined;
      }
    };

    // We schedule reconnect here on the case we're online but server dropped connection for some reason
    const scheduleReconnect = () => {
      if (!isReadyToReconnect()) return;
      const maxDelayMs = 30000;
      const baseDelayMs = 1000;
      const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
      attempt += 1;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      reconnectTimeout = setTimeout(() => {
        tryConnect();
      }, delay);
    };
    const handleDisconnect = () => scheduleReconnect();

    const handleOnline = () => {
      resetBackoff();
      tryConnect();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        resetBackoff();
        tryConnect();
      }
    };

    provider.on("disconnect", handleDisconnect);
    provider.on("connect", resetBackoff);

    provider.on("authenticationFailed", ({ reason }: onAuthenticationFailedParameters) => {
      if (reason.includes("auth-token-fetch-failed")) {
        provider.configuration.websocketProvider.disconnect();
        scheduleReconnect();
        return;
      }
      setYDoc(newYDoc);
      setError({ type: "noReadPermissions" });
    });

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    const newAwareness = new AwarenessModel(provider, auth);
    setAwareness(newAwareness);
    setYProvider(provider);

    cleanupReconnect = () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };

    return () => {
      cleanupReconnect();
      newAwareness.dispose();
      provider.destroy();
      newYDoc.destroy();
    };
  }, [docId, auth, auth.authTokenResolved, prefetchedDoc]);

  // Step 2: Initialize the DocumentModel with document and yjs.
  useEffect(() => {
    if (yDoc && !doc) {
      try {
        devTools.startCounter("create");
        const newDoc = new DocumentModel(docId, yDoc, awareness, yProvider, prefetchedDoc);
        if (!computedNodesEnabled) {
          newDoc.computedNodes.enabled = false;
        }
        newDoc.api.refetch();
        devTools.endCounter("create");
        runInAction(() => {
          if (newDoc.textNode.nodes.length === 0) {
            newDoc.editor.clearUndo();
          }

          rootStore.doc = newDoc;
        });

        newDoc.subscribe();

        newDoc.connectionStatus.setupConnectionStatusTracking();
        newDoc.connectionStatus.setupEditorChangeTracking(newDoc.editor.tiptap);

        setDoc(newDoc);
        devTools.startCounter("firstRender");
      } catch (e) {
        if (e instanceof InvalidSchemaVersionError) {
          setError({ type: "invalidSchemaVersion", message: e.message });
          Sentry.captureException(e);
        } else {
          throw e;
        }
      }
    }
    if (doc) {
      queueMicrotask(() => {
        if ("requestIdleCallback" in window && typeof requestIdleCallback === "function") {
          requestIdleCallback(() => {
            devTools.endCounter("firstRender");
          });
        } else {
          setTimeout(() => {
            devTools.endCounter("firstRender");
          }, 0);
        }
      });
    }
  }, [docId, yDoc, awareness, doc, prefetchedDoc, rootStore, yProvider, computedNodesEnabled]);

  useEffect(() => {
    if (!doc) return undefined;
    return reaction(
      () => !rootStore.classroom.isStudentIn(doc.team) && !!computedNodesEnabled,
      (enabled) => {
        runInAction(() => {
          doc.computedNodes.enabled = enabled;
        });
      },
      { fireImmediately: true },
    );
  }, [doc, rootStore, computedNodesEnabled]);

  useEffect(() => {
    if (doc) {
      return () => doc.dispose();
    }
    return () => {};
  }, [doc]);

  return { doc, error };
}
